import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Target } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

type Variant = "banner" | "inline" | "compact";

interface VisionBoardCtaProps {
  /** Surface for analytics + slightly tuned copy */
  context: "dashboard" | "report" | "journey";
  /** Personalised hook, e.g. top archetype, latest milestone */
  highlight?: string | null;
  /** Optional override headline */
  title?: string;
  /** Optional override body */
  description?: string;
  variant?: Variant;
}

function defaultCopy(context: VisionBoardCtaProps["context"], highlight?: string | null) {
  if (context === "report") {
    return {
      title: highlight
        ? `Pin your “${highlight}” direction to your Vision Board`
        : "Update your Vision Board for your latest assessment",
      description:
        "Translate today's report into a living plan — dreams, life wheel, and your Purpose Pack stay in sync with your results.",
      cta: "Update Vision Board",
    };
  }
  if (context === "journey") {
    return {
      title: highlight
        ? `Your Vision Board is your ${highlight} compass`
        : "Keep your Vision Board moving with your journey",
      description:
        "Each milestone unlocks new clarity. Refresh your dreams, life wheel, and Purpose Pack so YOMA can nudge you with the right next step.",
      cta: "Open Vision Board",
    };
  }
  return {
    title: highlight
      ? `Update your Vision Board after your latest ${highlight}`
      : "Update your Vision Board for your latest assessment",
    description:
      "Turn your career insights into a living plan — dreams, life wheel, and YOMA nudges all in one place.",
    cta: "Open Vision Board",
  };
}

export default function VisionBoardCta({
  context,
  highlight,
  title,
  description,
  variant = "banner",
}: VisionBoardCtaProps) {
  const copy = defaultCopy(context, highlight);
  const heading = title ?? copy.title;
  const body = description ?? copy.description;
  const trackHref = `/vision-board?from=${context}`;

  if (variant === "compact") {
    return (
      <Link
        to={trackHref}
        aria-label={heading}
        className="group block rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/10 via-card to-card p-4 hover:border-primary/50 transition"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <Target className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-display text-sm font-semibold text-foreground truncate">{heading}</p>
            <p className="text-xs text-muted-foreground truncate">{body}</p>
          </div>
          <ArrowRight className="h-4 w-4 text-primary shrink-0 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </Link>
    );
  }

  if (variant === "inline") {
    return (
      <Card className="border-primary/30 bg-gradient-to-br from-primary/8 via-card to-card">
        <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-2xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-foreground">{heading}</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-xl">{body}</p>
            </div>
          </div>
          <Button asChild className="shrink-0 min-h-11">
            <Link to={trackHref}>
              {copy.cta} <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card">
      <CardContent className="p-6 sm:p-7 flex flex-col md:flex-row gap-5 md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-2xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <Target className="h-6 w-6" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-primary font-semibold">
              Vision Board · Future Command Center
            </p>
            <h3 className="font-display text-xl sm:text-2xl font-bold text-foreground mt-1">
              {heading}
            </h3>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">{body}</p>
          </div>
        </div>
        <Button asChild size="lg" className="shrink-0">
          <Link to={trackHref}>
            {copy.cta} <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
